import { useState, type FormEvent } from 'react';
import type { EmailAdmin } from '@shared/gmail';
import { PUBSUB_TOPIC_PATTERN, PUSH_ACCOUNT_PATTERN } from '@shared/gmail';
import { ApiError, api, errorMessage } from '@/lib/api';
import { formatAgo, formatDate } from '@/lib/format';
import { openAdmin } from '@/lib/overlays';
import { toast } from '@/lib/toast';
import { Callout } from '@/ui/bits';
import { CopyButton, CopyField } from '@/ui/Copy';
import { InputField } from '@/ui/Field';
import { ExtLink, Guide, GuideStep, GuideSteps } from '@/ui/Guide';
import { Icon } from '@/ui/Icon';
import { refreshInboxes } from './queries';

// The step-by-step Google Cloud setup, with the client, topic and push account fields in the steps that need them.

const GMAIL_PUSH_ACCOUNT = 'gmail-api-push@system.gserviceaccount.com';

const LINKS = {
  project: 'https://console.cloud.google.com/projectcreate',
  apis: 'https://console.cloud.google.com/flows/enableapi?apiid=gmail.googleapis.com,calendar-json.googleapis.com,pubsub.googleapis.com',
  consent: 'https://console.developers.google.com/auth/branding',
  clients: 'https://console.developers.google.com/auth/clients',
  topics: 'https://console.cloud.google.com/cloudpubsub/topic/list',
  serviceAccounts: 'https://console.cloud.google.com/iam-admin/serviceaccounts',
  subscriptions: 'https://console.cloud.google.com/cloudpubsub/subscription/list',
};

export function GuideBox({ data }: { data: EmailAdmin }) {
  const configured = data.google.configured;
  const topicSaved = !!data.pubsub.topic;
  const accountSaved = !!data.pubsub.pushAccount;
  const pushArrived = !!data.pubsub.lastPushAt;
  const steps = [configured, configured, configured, configured, configured, topicSaved, topicSaved, topicSaved, accountSaved, topicSaved && accountSaved && pushArrived];

  return (
    <Guide
      title="Set up Gmail with Google Cloud"
      className="span-12"
      done={steps.filter(Boolean).length}
      total={steps.length}
      intro={configured ? undefined : 'About 15 minutes. Steps 3 and 7 need a Google Workspace admin.'}
    >
      <GuideSteps>
        <GuideStep done={steps[0]} title="Create a Google Cloud project for Clientbook" action={<ExtLink href={LINKS.project}>Open Google Cloud console</ExtLink>} />
        <GuideStep
          done={steps[1]}
          title="Enable the Gmail, Google Calendar and Cloud Pub/Sub APIs in that project"
          action={<ExtLink href={LINKS.apis}>Enable the APIs</ExtLink>}
        />
        <GuideStep
          done={steps[2]}
          title="Set up the OAuth consent screen and choose Internal as the audience"
          sub="Internal lets anyone in your Google Workspace connect. External apps need Google’s verification for Gmail access, which includes a paid security assessment."
          action={<ExtLink href={LINKS.consent}>Open the consent screen</ExtLink>}
        />
        <GuideStep
          done={steps[3]}
          title="Create an OAuth client of the type Web application, with this authorised redirect URI"
          action={<ExtLink href={LINKS.clients}>Open Clients</ExtLink>}
        >
          <CopyField value={data.redirectUri} what="Redirect URI" />
        </GuideStep>
        <GuideStep
          done={steps[4]}
          title="Paste the client ID and client secret here"
          sub="Google shows the secret only once, when you create the client. Download it then."
        >
          <GoogleClientForm data={data} />
        </GuideStep>
        <GuideStep
          done={steps[5]}
          title={
            <>
              Create a Pub/Sub topic in the same project, for example <code>clientbook-gmail</code>
            </>
          }
          action={<ExtLink href={LINKS.topics}>Open Pub/Sub topics</ExtLink>}
        />
        <GuideStep
          done={steps[6]}
          title={
            <>
              Grant <code>{GMAIL_PUSH_ACCOUNT}</code> the Pub/Sub Publisher role on the topic
            </>
          }
          sub="If your organisation limits sharing to its own domain, add an exception for this Google account first."
          action={<CopyButton text={GMAIL_PUSH_ACCOUNT} what="Address" label="Copy address" />}
        />
        <GuideStep done={steps[7]} title="Paste the topic name here" sub={topicSaved ? <span className="gm-mono">{data.pubsub.topic}</span> : undefined}>
          <TopicForm data={data} />
        </GuideStep>
        <GuideStep
          done={steps[8]}
          title={
            <>
              Create a service account in the same project for push, for example <code>clientbook-push</code>, and paste its email here
            </>
          }
          sub={accountSaved ? <span className="gm-mono">{data.pubsub.pushAccount}</span> : undefined}
          action={<ExtLink href={LINKS.serviceAccounts}>Open service accounts</ExtLink>}
        >
          <PushAccountForm data={data} />
        </GuideStep>
        <GuideStep
          done={steps[9]}
          title="Add a push subscription to the topic that delivers to this endpoint"
          sub={
            data.pubsub.pushUrl ? (
              <>
                Choose <b>Push</b> as the delivery type, turn on <b>Enable authentication</b> and pick the service account from step 9. Leave the audience empty.
                {pushArrived ? ` Last push ${formatAgo(data.pubsub.lastPushAt!).toLowerCase()}.` : null}
              </>
            ) : (
              'The endpoint appears here once the topic is saved.'
            )
          }
          action={topicSaved ? <ExtLink href={LINKS.subscriptions}>Open subscriptions</ExtLink> : undefined}
        >
          {data.pubsub.pushUrl ? <CopyField value={data.pubsub.pushUrl} what="Push endpoint" /> : null}
        </GuideStep>
      </GuideSteps>
      {!data.publicHttps ? (
        <Callout tone="warn" className="gm-guide-note" title="Push needs a public HTTPS address">
          This server runs at <b>{data.baseUrl}</b>, where Google cannot deliver push. Clientbook checks every connected inbox
          every few minutes instead, so mail still arrives, a little later.
        </Callout>
      ) : null}
      {data.pubsub.watchError ? (
        <Callout tone="warn" className="gm-guide-note" role="alert" title={`Google refused to start push ${formatAgo(data.pubsub.watchError.at).toLowerCase()}`}>
          {data.pubsub.watchError.message} Gmail needs {GMAIL_PUSH_ACCOUNT} to have the Pub/Sub Publisher role on the topic (step
          7). Once it has, save the topic again.
        </Callout>
      ) : null}
    </Guide>
  );
}

function GoogleClientForm({ data }: { data: EmailAdmin }) {
  const { clientId, clientSecret } = data.google;
  const [id, setId] = useState('');
  const [secret, setSecret] = useState('');
  const [errors, setErrors] = useState<{ clientId?: string; clientSecret?: string; form?: string }>({});
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(!data.google.configured);
  const missingKey = data.masterKey === 'missing';

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const next: typeof errors = {};
    const idValue = id.trim();
    const secretValue = secret.trim();
    if (!clientId.set && !idValue) next.clientId = 'Paste the client ID from Google.';
    if (!clientSecret.set && !secretValue) next.clientSecret = 'Paste the client secret from Google.';
    if (clientId.set && clientSecret.set && !idValue && !secretValue) next.form = 'Paste a new client ID or secret to replace the saved one.';
    setErrors(next);
    if (Object.keys(next).length) return;
    setBusy(true);
    try {
      await api.put('/gmail/admin/google', { clientId: idValue || undefined, clientSecret: secretValue || undefined });
      await refreshInboxes();
      setId('');
      setSecret('');
      setEditing(false);
      toast(data.google.configured ? 'Google client updated' : 'Google is set up. Everyone can connect Gmail now.', { icon: 'check-circle' });
    } catch (err) {
      if (err instanceof ApiError && (err.fields.clientId || err.fields.clientSecret)) {
        setErrors({ clientId: err.fields.clientId, clientSecret: err.fields.clientSecret });
      } else {
        setErrors({ form: errorMessage(err) });
      }
    } finally {
      setBusy(false);
    }
  };

  if (missingKey) {
    return (
      <Callout
        tone="warn"
        title="Set the server’s master key first"
        actions={
          <button type="button" className="btn btn-sm btn-tile" onClick={() => openAdmin('security')}>
            <Icon name="arrow-right" />
            Open Security
          </button>
        }
      >
        Clientbook stores the client secret sealed with it.
      </Callout>
    );
  }

  if (!editing) {
    return (
      <div className="gm-saved">
        <dl className="kv">
          <dt>Client ID</dt>
          <dd className="gm-mono">{clientId.hint ?? 'Saved'}</dd>
          <dt>Client secret</dt>
          <dd className="gm-mono">
            {clientSecret.hint ?? 'Saved'}
            {clientSecret.updatedAt ? <span className="muted"> · saved {formatDate(clientSecret.updatedAt)}</span> : null}
          </dd>
        </dl>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(true)}>
          <Icon name="key" />
          Replace
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={save} noValidate>
      <div className="gm-pair">
        <InputField
          label="Client ID"
          value={id}
          onChange={(e) => setId(e.target.value)}
          placeholder={clientId.hint ?? '1234567890-abc123.apps.googleusercontent.com'}
          autoComplete="off"
          spellCheck={false}
          error={errors.clientId}
          help={clientId.set ? 'Saved. Leave empty to keep it.' : undefined}
        />
        <InputField
          label="Client secret"
          type="password"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          placeholder={clientSecret.hint ?? 'GOCSPX-…'}
          autoComplete="new-password"
          spellCheck={false}
          error={errors.clientSecret}
          help={clientSecret.set ? 'Saved and sealed. Leave empty to keep it.' : undefined}
        />
      </div>
      <div className="gm-form-foot">
        {errors.form ? (
          <p className="field-err" role="alert">
            <Icon name="warn" className="i-xs" />
            {errors.form}
          </p>
        ) : (
          <span />
        )}
        <span className="spacer" />
        {data.google.configured ? (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setEditing(false);
              setErrors({});
              setId('');
              setSecret('');
            }}
          >
            Cancel
          </button>
        ) : null}
        <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>
          {busy ? 'Saving' : 'Save'}
        </button>
      </div>
    </form>
  );
}

function TopicForm({ data }: { data: EmailAdmin }) {
  const saved = data.pubsub.topic;
  const [topic, setTopic] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<'save' | 'remove' | null>(null);
  const [editing, setEditing] = useState(!saved);

  const submit = async (value: string | null) => {
    if (busy) return;
    if (value !== null) {
      const v = value.trim();
      if (!v) return setError('Paste the topic name from Google Cloud.');
      if (!PUBSUB_TOPIC_PATTERN.test(v)) return setError('Use the full topic name, like projects/your-project/topics/clientbook-gmail.');
    }
    setBusy(value === null ? 'remove' : 'save');
    setError(null);
    try {
      await api.put('/gmail/admin/pubsub', { topic: value === null ? null : value.trim() });
      await refreshInboxes();
      setTopic('');
      setEditing(value === null);
      toast(value === null ? 'Push turned off. Clientbook checks inboxes every few minutes.' : 'Topic saved. Google starts announcing new mail.', {
        icon: value === null ? 'clock' : 'zap',
      });
    } catch (err) {
      setError(err instanceof ApiError && err.fields.topic ? err.fields.topic : errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  if (!data.google.configured) {
    return <p className="field-help">Save the Google client in step 5 first.</p>;
  }

  if (saved && !editing) {
    return (
      <div className="gm-form-foot gm-topic-acts">
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(true)}>
          Change
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => submit(null)} disabled={!!busy} aria-busy={busy === 'remove' || undefined}>
          {busy === 'remove' ? 'Turning off' : 'Turn off push'}
        </button>
        {error ? (
          <p className="field-err" role="alert">
            <Icon name="warn" className="i-xs" />
            {error}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit(topic);
      }}
      noValidate
    >
      <InputField
        label="Topic name"
        value={topic}
        onChange={(e) => setTopic(e.target.value)}
        placeholder="projects/your-project/topics/clientbook-gmail"
        autoComplete="off"
        spellCheck={false}
        error={error}
        inputClassName="gm-mono"
      />
      <div className="gm-form-foot">
        <span className="spacer" />
        {saved ? (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setEditing(false);
              setError(null);
              setTopic('');
            }}
          >
            Cancel
          </button>
        ) : null}
        <button type="submit" className="btn btn-primary" disabled={!!busy} aria-busy={busy === 'save' || undefined}>
          {busy === 'save' ? 'Saving' : 'Save topic'}
        </button>
      </div>
    </form>
  );
}

function PushAccountForm({ data }: { data: EmailAdmin }) {
  const saved = data.pubsub.pushAccount;
  const [account, setAccount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(!saved);

  const submit = async () => {
    if (busy) return;
    const v = account.trim().toLowerCase();
    if (!v) return setError('Paste the service account’s email from Google Cloud.');
    if (!PUSH_ACCOUNT_PATTERN.test(v)) return setError('Use the service account’s email, like clientbook-push@your-project.iam.gserviceaccount.com.');
    setBusy(true);
    setError(null);
    try {
      await api.put('/gmail/admin/pubsub/account', { account: v });
      await refreshInboxes();
      setAccount('');
      setEditing(false);
      toast('Push service account saved', { icon: 'check-circle' });
    } catch (err) {
      setError(err instanceof ApiError && err.fields.account ? err.fields.account : errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!data.google.configured) {
    return <p className="field-help">Save the Google client in step 5 first.</p>;
  }

  if (saved && !editing) {
    return (
      <div className="gm-form-foot gm-topic-acts">
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(true)}>
          Change
        </button>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      noValidate
    >
      <InputField
        label="Service account email"
        value={account}
        onChange={(e) => setAccount(e.target.value)}
        placeholder="clientbook-push@your-project.iam.gserviceaccount.com"
        autoComplete="off"
        spellCheck={false}
        error={error}
        inputClassName="gm-mono"
      />
      <div className="gm-form-foot">
        <span className="spacer" />
        {saved ? (
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => {
              setEditing(false);
              setError(null);
              setAccount('');
            }}
          >
            Cancel
          </button>
        ) : null}
        <button type="submit" className="btn btn-primary" disabled={busy} aria-busy={busy || undefined}>
          {busy ? 'Saving' : 'Save account'}
        </button>
      </div>
    </form>
  );
}
